/**
 * Markdown/HTML Rendering Utility
 * Converts markdown text to React elements with proper formatting
 */

import React from 'react';

/**
 * Converts markdown text to React elements with proper HTML formatting
 * Supports: **bold**, *italic*, headings (# ## ###), numbered/bullet lists, paragraphs, code blocks
 */
export function renderFormattedText(text: string): React.ReactNode {
  if (!text) return null;

  // Remove code block markers (```markdown ... ``` or ``` ... ```)
  let cleanedText = text.replace(/```markdown\s*\n?/gi, '').replace(/```\s*\n?/g, '');
  
  // Split by double newlines to get paragraphs
  const paragraphs = cleanedText.split(/\n\n+/).filter(p => p.trim().length > 0);
  
  return (
    <div className="space-y-3">
      {paragraphs.map((paragraph, idx) => {
        const trimmed = paragraph.trim();
        if (!trimmed) return null;
        
        // Check if it's a heading
        if (trimmed.startsWith('### ')) {
          return (
            <h3 key={idx} className="text-base font-bold text-gray-900 mt-4 mb-2">
              {formatInline(trimmed.substring(4))}
            </h3>
          );
        }
        if (trimmed.startsWith('## ')) {
          return (
            <h2 key={idx} className="text-lg font-bold text-gray-900 mt-4 mb-2">
              {formatInline(trimmed.substring(3))}
            </h2>
          );
        }
        if (trimmed.startsWith('# ')) {
          return (
            <h1 key={idx} className="text-xl font-bold text-gray-900 mt-4 mb-2">
              {formatInline(trimmed.substring(2))}
            </h1>
          );
        }
        
        // Check if it's a numbered list
        if (trimmed.match(/^[\d]+\.\s/)) {
          const items = trimmed.split(/\n(?=[\d]+\.\s)/);
          return (
            <ol key={idx} className="list-decimal list-inside space-y-2 ml-2">
              {items.map((item, itemIdx) => {
                const cleaned = item.replace(/^[\d]+\.\s/, '').trim();
                return (
                  <li key={itemIdx} className="text-gray-700">
                    {formatInline(cleaned)}
                  </li>
                );
              })}
            </ol>
          );
        }
        
        // Check if it's a bullet list
        if (trimmed.match(/^[-*]\s/)) {
          const items = trimmed.split(/\n(?=[-*]\s)/);
          return (
            <ul key={idx} className="list-disc list-inside space-y-2 ml-2">
              {items.map((item, itemIdx) => {
                const cleaned = item.replace(/^[-*]\s/, '').trim();
                return (
                  <li key={itemIdx} className="text-gray-700">
                    {formatInline(cleaned)}
                  </li>
                );
              })}
            </ul>
          );
        }
        
        // Regular paragraph
        return (
          <p key={idx} className="text-gray-700 leading-relaxed">
            {formatInline(trimmed)}
          </p>
        );
      })}
    </div>
  );
}

/**
 * Formats inline markdown (bold, italic) within text
 */
function formatInline(text: string): React.ReactNode {
  if (!text) return null;
  
  // Process bold first (**text**)
  const boldRegex = /\*\*([^*]+)\*\*/g;
  const segments: Array<{ type: 'text' | 'bold' | 'italic'; content: string }> = [];
  let lastPos = 0;
  let match;
  
  while ((match = boldRegex.exec(text)) !== null) {
    if (match.index > lastPos) {
      segments.push({ type: 'text', content: text.substring(lastPos, match.index) });
    }
    segments.push({ type: 'bold', content: match[1] });
    lastPos = match.index + match[0].length;
  }
  if (lastPos < text.length) {
    segments.push({ type: 'text', content: text.substring(lastPos) });
  }
  
  // Now process italic in text segments
  const result: React.ReactNode[] = [];
  segments.forEach((segment, idx) => {
    if (segment.type === 'bold') {
      result.push(
        <strong key={`bold-${idx}`} className="font-semibold">
          {segment.content}
        </strong>
      );
    } else if (segment.type === 'text') {
      // Process italic in text segments
      const italicRegex = /\*([^*]+)\*/g;
      let italicMatch;
      let textLastPos = 0;
      let hasItalic = false;
      
      while ((italicMatch = italicRegex.exec(segment.content)) !== null) {
        hasItalic = true;
        if (italicMatch.index > textLastPos) {
          result.push(segment.content.substring(textLastPos, italicMatch.index));
        }
        result.push(
          <em key={`italic-${idx}-${italicMatch.index}`} className="italic">
            {italicMatch[1]}
          </em>
        );
        textLastPos = italicRegex.lastIndex;
      }
      
      if (hasItalic && textLastPos < segment.content.length) {
        result.push(segment.content.substring(textLastPos));
      } else if (!hasItalic) {
        result.push(segment.content);
      }
    }
  });
  
  return result.length > 0 ? <>{result}</> : text;
}
